/**
 * Authentication Helper Utilities
 * 
 * Utility functions to make external auth flows easier to handle.
 */

import React from 'react';
import { setSupabaseSession } from '../core/auth.js';

/**
 * Handle external auth return with token from URL
 * This is a complete solution for handling funnel returns
 * 
 * @param {Object} options - Configuration options
 * @param {string} [options.tokenParam='token'] - URL parameter name for the token
 * @param {boolean} [options.cleanUrl=true] - Whether to clean the URL after processing
 * @param {string} [options.redirectPath=null] - Path to redirect to after success
 * @param {number} [options.timeout=5000] - Timeout in ms to wait for auth state update
 * @param {Function} [options.onSuccess] - Callback fired on successful auth
 * @param {Function} [options.onError] - Callback fired on error
 * @param {Function} [options.onNoToken] - Callback fired when no token found
 * @returns {Promise<Object>} Result object with status and details
 */
export const handleExternalAuthReturn = async (options = {}) => {
  const {
    tokenParam = 'token',
    cleanUrl = true,
    redirectPath = null,
    timeout = 5000,
    onSuccess = null,
    onError = null,
    onNoToken = null
  } = options;

  const result = {
    success: false,
    status: 'checking',
    message: '',
    token: null,
    error: null
  };

  try {
    // Get token from URL
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get(tokenParam) || urlParams.get('access_token');
    
    if (!token) {
      result.status = 'no_token';
      result.message = 'No authentication token found in URL';
      
      if (onNoToken) {
        await onNoToken(result);
      }
      
      return result;
    }

    result.token = token;
    result.status = 'processing';
    result.message = 'Setting Supabase session...';

    console.log('🔄 Processing external auth token...');

    // Set the Supabase session
    const sessionSuccess = await setSupabaseSession(token);

    if (!sessionSuccess) {
      throw new Error('Failed to set Supabase session');
    }

    console.log('✅ Supabase session set successfully');

    // Clean up URL if requested
    if (cleanUrl) {
      const cleanUrl = new URL(window.location);
      cleanUrl.searchParams.delete(tokenParam);
      cleanUrl.searchParams.delete('access_token');
      cleanUrl.searchParams.delete('refresh_token');
      cleanUrl.searchParams.delete('expires_at');
      
      window.history.replaceState({}, document.title, cleanUrl.toString());
      console.log('🧹 URL cleaned up');
    }

    result.success = true;
    result.status = 'success';
    result.message = 'Authentication successful';

    // Fire success callback
    if (onSuccess) {
      await onSuccess(result);
    }

    // Redirect if requested
    if (redirectPath) {
      setTimeout(() => {
        window.location.href = redirectPath;
      }, 500); // Small delay to ensure state is updated
    }

    return result;

  } catch (error) {
    console.error('❌ External auth error:', error);
    
    result.success = false;
    result.status = 'error';
    result.message = error.message || 'Authentication failed';
    result.error = error;

    // Fire error callback
    if (onError) {
      await onError(result);
    }

    return result;
  }
};

/**
 * Wait for authentication state to change
 * Useful when you need to wait for the SDK to update after setting a session
 * 
 * @param {Function} getAuthState - Function that returns current auth state
 * @param {boolean} [targetAuthenticated=true] - Target authentication state
 * @param {number} [timeout=5000] - Timeout in milliseconds
 * @returns {Promise<boolean>} True if state changed to target, false if timeout
 */
export const waitForAuthStateChange = async (getAuthState, targetAuthenticated = true, timeout = 5000) => {
  const startTime = Date.now();
  
  while (Date.now() - startTime < timeout) {
    const { isAuthenticated, user } = getAuthState();
    
    if (isAuthenticated === targetAuthenticated && (targetAuthenticated ? user : true)) {
      return true;
    }
    
    // Wait a bit before checking again
    await new Promise(resolve => setTimeout(resolve, 100));
  }
  
  return false;
};

/**
 * Extract auth tokens from URL parameters
 * 
 * @param {string} [url] - URL to parse (defaults to current URL)
 * @returns {Object} Object with extracted tokens
 */
export const extractTokensFromUrl = (url = window.location.href) => {
  const urlObj = new URL(url);
  const params = urlObj.searchParams;
  
  // Debug: log all URL parameters to see what we're getting
  const allParams = {};
  for (const [key, value] of params.entries()) {
    allParams[key] = value.substring(0, 20) + (value.length > 20 ? '...' : ''); // Truncate for security
  }
  console.log('🔍 All URL parameters:', allParams);
  
  // Check for various token parameter names that might be used
  const accessToken = params.get('token') || 
                     params.get('access_token') || 
                     params.get('supabase_token') ||
                     params.get('auth_token');
                     
  // Also check URL hash fragments (sometimes tokens are in #token=... instead of ?token=...)
  const hashParams = new URLSearchParams(urlObj.hash.substring(1));
  const hashAccessToken = hashParams.get('access_token') || hashParams.get('token');
  
  const finalAccessToken = accessToken || hashAccessToken;
  
  const result = {
    accessToken: finalAccessToken,
    refreshToken: params.get('refresh_token') || hashParams.get('refresh_token'),
    expiresAt: params.get('expires_at') || hashParams.get('expires_at'),
    hasTokens: !!finalAccessToken
  };
  
  console.log('🎯 Extracted token info:', {
    hasAccessToken: !!result.accessToken,
    hasRefreshToken: !!result.refreshToken,
    hasExpiresAt: !!result.expiresAt,
    hasTokens: result.hasTokens
  });
  
  return result;
};

/**
 * Check if current URL contains auth tokens
 * 
 * @returns {boolean} True if auth tokens are present
 */
export const hasAuthTokensInUrl = () => {
  return extractTokensFromUrl().hasTokens;
};

/**
 * Simple React hook for handling external auth returns
 * 
 * @param {Object} options - Same options as handleExternalAuthReturn
 * @returns {Object} State object with loading, error, success states
 */
export const useExternalAuthReturn = (options = {}) => {
  if (typeof window === 'undefined') {
    // SSR safety
    return {
      isLoading: false,
      isSuccess: false,
      isError: false,
      error: null,
      result: null
    };
  }

  const [state, setState] = React.useState({
    isLoading: false,
    isSuccess: false,
    isError: false,
    error: null,
    result: null
  });

  React.useEffect(() => {
    const processAuth = async () => {
      if (!hasAuthTokensInUrl()) {
        return; // No tokens to process
      }

      setState(prev => ({ ...prev, isLoading: true }));

      try {
        const result = await handleExternalAuthReturn(options);
        
        setState({
          isLoading: false,
          isSuccess: result.success,
          isError: !result.success,
          error: result.error,
          result
        });
      } catch (error) {
        setState({
          isLoading: false,
          isSuccess: false,
          isError: true,
          error,
          result: null
        });
      }
    };

    processAuth();
  }, []); // Empty dependency array - only run once

  return state;
};

// Re-export for convenience
export { setSupabaseSession } from '../core/auth.js';
